import React, { useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'material-ui-snackbar-provider';
import VideocamIcon from '@mui/icons-material/Videocam';
import { useHistory } from 'react-router-dom';

import { ACCESS_TYPE, ACCESS_TYPE_ENUM } from '../../../enums/ACCESS_ENUM';
import { ReactComponent as ComposerIcon } from '../../../assets/icons/composer.svg';
import { postVideofeedViewRequest, updateVideofeedTitle } from '../../../api';
import { updateFeedToShare, updateShowShareModal } from '../../../store/videoFeedsList/videoFeedsListSlice';
import { useAppDispatch } from '../../../store/hooks';
import { NOTIFICATION, VIDEOFEED_TYPE, VIDEOPAGE_TYPE } from '../../../enums/ENUMS';
import { clearVideoFeeds } from '../../../store/videos/videoSlice';
import { DateFormatter } from '../../../helpers/dateFormatter';
import StorageIcon from '../../../assets/icons/storage.svg';
import Icon from '../../VideofeedsList/Videofeed/icon';
import TrashButton from '../../modules/TrashButton';
import { VIDEOFEED } from '../../../enums/TYPES';
import { PATHS } from '../../../enums/PATHS';
import styles from './AllContentComposer.module.scss';
import NotReady from '../../../assets/icons/not-ready';
import { useDimensions } from '../../../helpers';
import { Tooltip } from '@material-ui/core';
import DeleteComposer from '../DeleteComposer';
import { useOnClickOutside } from 'usehooks-ts';
import EditButton from '../../modules/EditButton';
import { MoreIcon } from '../../../assets/icons/threedots';
import ShareButton from '../../modules/ShareButton';
import { SortOptions } from '../../../enums/SORT';
import { getDateColumnTooltipLabel, getDateColumnTooltipValue, getDateColumnValue } from '../../modules/DateSortHeader';
import FileTypeThumbnail from '../../modules/FileTypeThumbnail';
import ReplaceThumbnailModal from '../../modules/ReplaceThumbnailModal';
import { removeVideofeedThumbnail, uploadVideofeedThumbnail } from '../../../api/videofeeds';
import ThumbnailButton from '../../modules/ThumbnailButton';

type TComposer = {
  composer: VIDEOFEED;
  owner?: boolean;
  feedIndex?: number;
  sharedComposer?: boolean;
  noActions?: boolean;
  fetchDataCallback?: () => void;
  dateSortOption?: SortOptions | null;
};

const AllContentComposer: React.FC<TComposer> = ({
  composer,
  owner = true,
  feedIndex,
  sharedComposer,
  noActions,
  fetchDataCallback,
  dateSortOption,
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const dispatch = useAppDispatch();
  const notification = useSnackbar();
  const history = useHistory();
  const [title, setTitle] = useState(composer.title);
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const actionMenuRef = useRef<HTMLDivElement | null>(null);
  const [showDotsMenu, setShowDotsMenu] = useState(false);
  const [showThumbnailModal, setShowThumbnailModal] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState(composer.thumbnail_url || '');
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const previewUrl = thumbnailUrl || composer?.videos?.[0]?.s3_preview_url || '';

  useEffect(() => {
    setThumbnailUrl(composer.thumbnail_url || '');
  }, [composer.thumbnail_url]);

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
  };

  const handleEdit = async () => {
    if (title.trim().length === 0) {
      setTitle(composer.title);
      setTimeout(() => {
        notification.showMessage(NOTIFICATION.EMPTY);
      }, 100);

      return;
    }

    setIsEditing(!isEditing);

    if (title.trim() === composer.title.trim()) return;

    await updateVideofeedTitle(composer.id, title);
    if (fetchDataCallback) {
      fetchDataCallback();
    }
  };

  const handleShare = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch(updateFeedToShare(composer));
    dispatch(updateShowShareModal(true));
    setShowDotsMenu(false);
  };

  const onReplaceThumbnail = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDotsMenu(false);
    setShowThumbnailModal(true);
  };

  const switchDotsMenu = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (showDotsMenu) {
      setShowDotsMenu(false);
      return;
    }

    const feedElement = e.currentTarget.closest(`.${styles.container}`) as HTMLElement;
    if (!feedElement) return;

    const feedRect = feedElement.getBoundingClientRect();
    const menuWidth = actionMenuRef.current?.offsetWidth || 310;
    const menuHeight = actionMenuRef.current?.offsetHeight || 300;

    let posX = e.clientX - feedRect.left;
    let posY = e.clientY - feedRect.top;

    // Ensure the menu stays within the feed boundaries
    if (posX + menuWidth > feedRect.width) {
      posX = feedRect.width - menuWidth - 10;
    }

    // Adjust if the menu would go below the viewport
    if (feedRect.bottom + menuHeight > window.innerHeight) {
      posY -= menuHeight - 155; // Move it upwards
    }

    setMenuPosition({
      x: feedRect.left + posX,
      y: feedRect.top + posY,
    });

    setShowDotsMenu(true);
  };

  const enableComposerNameEdit = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDotsMenu(false);
    setIsEditing(true);
    setTimeout(() => {
      if (inputRef && inputRef?.current) {
        inputRef.current.focus();
      }
    }, 150);
  };

  const switchDeleteModal = () => {
    setShowDeleteModal(!showDeleteModal);
  };

  const handleDeleteClick = (e?: any) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    setShowDotsMenu(false);
    setShowDeleteModal(true);
  };

  type CustomEventHandler = React.MouseEvent<HTMLDivElement> | React.MouseEvent<SVGSVGElement>;
  const redirectToVideoPage = (e: CustomEventHandler) => {
    if (noActions) return;
    if (e.currentTarget.contains(e.target as Node)) {
      postVideofeedViewRequest(composer.id);
      history.push(
        `${PATHS.COMPOSER_PAGE.path}?feedId=${composer.id}&allContent=true&fileside=true&feed=${feedIndex}&clipId=${
          composer.videos[0].id
        }&clip=0&pageType=${VIDEOPAGE_TYPE.MY_COMPOSER}${sharedComposer ? '&sharedComposer=true' : ''}`,
      );
    }
    dispatch(clearVideoFeeds());
  };

  const { widthD } = useDimensions();

  useOnClickOutside(actionMenuRef, (e) => {
    e.stopPropagation();
    setShowDotsMenu(false);
  });

  return (
    <div className={styles.container} onClick={redirectToVideoPage} onContextMenu={switchDotsMenu}>
      <div className={styles.leftSide} onClick={redirectToVideoPage}>
        <div className={styles.nameAndImgContainer} onClick={redirectToVideoPage}>
          <FileTypeThumbnail
            className={styles.fileTypeThumbnail}
            icon={
              <Icon onClick={redirectToVideoPage}>
                <ComposerIcon
                  className={styles.composerIcon}
                  style={{
                    display: 'flex',
                    pointerEvents: 'none',
                    color: composer?.type === VIDEOFEED_TYPE.MY_COMPOSER ? '#39b3ea' : undefined,
                  }}
                  onClick={redirectToVideoPage}
                />
              </Icon>
            }
          >
            {previewUrl ? (
              <img src={previewUrl} alt={'video feed'} className={styles.thumbnail} />
            ) : composer?.videos?.length > 0 ? (
              <VideocamIcon className={styles.thumbnail} />
            ) : (
              <NotReady className={styles.thumbnail} />
            )}
          </FileTypeThumbnail>
          <div />

          <Tooltip title={composer.title}>
            {isEditing ? (
              <form
                onSubmit={handleEdit}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <input ref={inputRef} className={styles.input} onChange={handleChange} defaultValue={composer.title} onBlur={handleEdit} />
              </form>
            ) : (
              <div className={styles.name}>{composer.title}</div>
            )}
          </Tooltip>
        </div>
      </div>
      <div className={styles.rightSide}>
        <Tooltip placement="bottom-start" title={composer?.created_by?.username}>
          <div className={styles.owner}>{composer?.created_by?.username}</div>
        </Tooltip>

        <Tooltip
          placement="bottom-start"
          title={composer?.highest_user_access ? ACCESS_TYPE[composer?.highest_user_access?.access?.title as ACCESS_TYPE_ENUM] : 'VIEW'}
        >
          <div className={styles.owner}>
            {composer?.highest_user_access ? ACCESS_TYPE[composer?.highest_user_access?.access?.title as ACCESS_TYPE_ENUM] : 'VIEW'}
          </div>
        </Tooltip>
        <div className={styles.owner}>Online Composers</div>
        <Tooltip title={`${getDateColumnTooltipLabel(dateSortOption)}: ${DateFormatter(getDateColumnTooltipValue(composer, dateSortOption), true)}`}>
          <div className={styles.date}>
            {widthD > 1600
              ? DateFormatter(getDateColumnValue(composer, dateSortOption), true)
              : DateFormatter(getDateColumnValue(composer, dateSortOption), false)}
          </div>
        </Tooltip>
        <div className={styles.size}>
          <img src={StorageIcon} alt="storage" />
          0.01 MB
        </div>

        <div className={styles.duration}></div>

        <div className={styles.dotsMenuBtn}>
          <div className={styles.dotIcon} onClick={switchDotsMenu}>
            <MoreIcon />
          </div>
        </div>
      </div>

      <div
        ref={actionMenuRef}
        className={styles.actionMenu}
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'absolute',
          display: showDotsMenu ? 'block' : 'none',
          top: `${menuPosition.y}px`,
          left: `${menuPosition.x}px`,
        }}
      >
        <div className={styles.menuOption} onClick={enableComposerNameEdit}>
          <EditButton /> Rename
        </div>
        <div className={styles.menuOption} onClick={handleShare}>
          <ShareButton /> Share
        </div>
        <div className={styles.menuOption} onClick={onReplaceThumbnail}>
          <ThumbnailButton /> Replace Thumbnail Image
        </div>
        <div className={styles.menuOption} onClick={handleDeleteClick}>
          <TrashButton /> Delete
        </div>
      </div>

      <ReplaceThumbnailModal
        open={showThumbnailModal}
        setOpen={setShowThumbnailModal}
        onUpload={(data) => uploadVideofeedThumbnail(composer.id, data)}
        onUploaded={(updatedComposer) => setThumbnailUrl(updatedComposer.thumbnail_url || '')}
        onRestore={thumbnailUrl ? () => removeVideofeedThumbnail(composer.id) : undefined}
        preview={{ type: 'file', title, imageUrl: previewUrl }}
      />

      <DeleteComposer
        owner={composer.created_by}
        open={showDeleteModal}
        switchModal={switchDeleteModal}
        folderId={composer.id}
        fetchDataCallback={fetchDataCallback}
      />
    </div>
  );
};

export default AllContentComposer;
